/**
 * Pure helpers for the Staff Profile page's shared view-filter + subject-filter
 * state. Kept out of the component so the filter/scroll/count logic is unit
 * testable without React.
 */
import { compareGradeLabels } from "../../../utils/gradeOrder";

export type StaffView = "today" | "week" | "calendar" | "students" | "subjects" | "classes" | "class-schedules";

export const STAFF_VIEWS: StaffView[] = ["today", "week", "calendar", "students", "subjects", "classes", "class-schedules"];

/** Alias for the "Class Schedules" tab — the spec uses ?tab=ClassSchedules,
 *  and the top "13 Classes" pill also opens this tab. We normalise both aliases
 *  into the same internal view value so URL state is single-source. */
export const CLASS_SCHEDULES_VIEW: StaffView = "class-schedules";
export const CLASS_SCHEDULES_TAB_PARAM = "ClassSchedules";

/** Section element id a summary/progress filter scrolls to. */
export const SECTION_IDS = {
  today: "staff-today-schedule",
  weekly: "staff-weekly-schedule",
  assignments: "staff-teaching-assignments",
} as const;

/** Parse a raw ?view= param into a valid view (or "" when absent/invalid).
 *  Legacy "month" maps to the calendar view. */
export const parseView = (raw?: string | null): StaffView | "" => {
  if (raw === "month") return "calendar";
  if (raw === "ClassSchedules" || raw === "classSchedules") return CLASS_SCHEDULES_VIEW;
  return raw && (STAFF_VIEWS as string[]).includes(raw) ? (raw as StaffView) : "";
};

/** True when the URL asks for the dedicated "Class Schedules" tab
 *  (either `?view=class-schedules` or the spec-preferred `?tab=ClassSchedules`). */
export const isClassSchedulesTab = (searchParams: URLSearchParams): boolean =>
  parseView(searchParams.get("view")) === CLASS_SCHEDULES_VIEW
  || searchParams.get("tab") === CLASS_SCHEDULES_TAB_PARAM;

/** Which on-page section a given view scrolls to / highlights. */
export const viewTargetId = (view: StaffView): string => {
  switch (view) {
    case "today":
      return SECTION_IDS.today;
    case "week":
    case "calendar":
      return SECTION_IDS.weekly;
    case "students":
    case "subjects":
    case "classes":
      return SECTION_IDS.assignments;
    case "class-schedules":
      return "staff-class-schedules-tab";
    default:
      return "";
  }
};

/** The planner sub-view (week grid vs monthly calendar) for a page view. */
export const plannerMode = (view: StaffView | ""): "week" | "calendar" =>
  view === "calendar" ? "calendar" : "week";

/** URL-driven planner view for the Weekly Teaching Schedule section.
 *  The page opens in "calendar" unless the URL explicitly requests "week" —
 *  users mainly come here to see this staff member's dated occurrences, so
 *  the monthly grid is the sensible default and any other value falls back to
 *  it. Kept independent of `?view=` (the top-pill filter) so that switching
 *  filter pills never silently changes the planner mode. */
export type ScheduleView = "week" | "calendar";
export const SCHEDULE_VIEW_PARAM = "schedule_view";
export const parseScheduleView = (raw?: string | null): ScheduleView =>
  raw === "week" ? "week" : "calendar";

export const isWeeklyView = (view: StaffView | ""): boolean => view === "week";
export const isCalendarView = (view: StaffView | ""): boolean => view === "calendar";
export const isAssignmentsView = (view: StaffView | ""): boolean =>
  view === "students" || view === "subjects" || view === "classes";

/** Invert the backend `grade_subjects` payload (grade → subjects[]) into a
 *  subject-first capability list (subject → grades[]) for the Teaching
 *  Assignments section. This is teaching-capability data — merged from active
 *  class templates AND the staff_grades_subject_tables pivot — not a class
 *  list. Subjects are sorted alphabetically; grades follow the canonical
 *  Pre-K → JK → SK → 1 → … → 12 → Adult sequence. */
export interface TeachingCapabilitySubject {
  id: number;
  name: string;
  grades: Array<{ id: number; name: string }>;
}

export const invertGradeSubjectsToSubjects = (
  gradeSubjects: any[],
): TeachingCapabilitySubject[] => {
  const bySubject = new Map<number, TeachingCapabilitySubject & { _gradeIds: Set<number> }>();
  (gradeSubjects ?? []).forEach((g: any) => {
    const gradeId = Number(g?.grade_id);
    const gradeName = String(g?.grade ?? `Grade #${gradeId}`);
    if (!Number.isFinite(gradeId)) return;
    (g?.subjects ?? []).forEach((s: any) => {
      const sid = Number(s?.id);
      if (!Number.isFinite(sid)) return;
      const name = String(s?.name ?? `Subject #${sid}`);
      let entry = bySubject.get(sid);
      if (!entry) {
        entry = { id: sid, name, grades: [], _gradeIds: new Set<number>() };
        bySubject.set(sid, entry);
      }
      if (!entry._gradeIds.has(gradeId)) {
        entry._gradeIds.add(gradeId);
        entry.grades.push({ id: gradeId, name: gradeName });
      }
    });
  });
  return Array.from(bySubject.values())
    .map(({ _gradeIds, ...rest }) => ({
      ...rest,
      grades: rest.grades.slice().sort((a, b) => compareGradeLabels(a.name, b.name)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
